import { useQuery } from '@tanstack/react-query';
import { post } from '../api-client';
import type { ProviderDetailResponse } from '@/types/scheduling-api';

interface AvailableProvidersParams {
  organizationId: string;
  practiceId: string;
  locationId?: string;
  /** Day to look up in YYYY-MM-DD; the hook expands to start/end of day. */
  date: string;
  searchText?: string;
  /** Empty array = all practitioners (mirrors Flutter PM behavior). */
  practitioners?: string[];
}

interface AvailableProvidersBody {
  organizationId: string;
  practiceId: string;
  locationId?: string;
  startDate: string;
  endDate: string;
  practitioners: string[];
  searchText?: string;
}

function toDayRange(date: string): { startDate: string; endDate: string } {
  return {
    startDate: `${date}T00:00:00.000`,
    endDate: `${date}T23:59:59.000`,
  };
}

export function useAvailableProviders(params: AvailableProvidersParams | null) {
  return useQuery({
    queryKey: ['available-providers', params],
    queryFn: ({ signal }) => {
      if (!params) throw new Error('useAvailableProviders called without params');
      const body: AvailableProvidersBody = {
        organizationId: params.organizationId,
        practiceId: params.practiceId,
        ...(params.locationId ? { locationId: params.locationId } : {}),
        ...toDayRange(params.date),
        practitioners: params.practitioners ?? [],
        ...(params.searchText ? { searchText: params.searchText } : {}),
      };
      return post<ProviderDetailResponse[]>('/api/Doctor/AvailableProviders', body, { signal });
    },
    enabled: !!params?.practiceId,
    // Calendar opens often during a shift — show cached data instantly,
    // then always trigger a fresh fetch on page mount so the strip
    // reflects current availability.
    refetchOnMount: 'always',
    // Opt out of the generic "An error occurred" toast. Flutter folds an
    // AvailableProviders failure to an empty list and shows the friendly
    // "No providers available" message instead of the raw error
    // (scheduling_bloc.dart:703-709). The consumer (scheduling-page) owns
    // that toast on error-or-empty, so suppress the global one here to
    // avoid double-toasting.
    meta: { skipGlobalError: true },
  });
}
